import { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { PlaceAutocomplete, TripSkeleton, EmptyState, IC } from "../shared";
import TripCard from "../Trips/TripCard";
import TripDetail from "../Trips/TripDetails";

const DesktopFind = ({ currentUser }) => {
  const [from, setFrom]             = useState("");
  const [to, setTo]                 = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [fromCoords, setFromCoords] = useState(null);
  const [trips, setTrips]           = useState([]);
  const [myReqs, setMyReqs]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [detail, setDetail]         = useState(null);
  const [routeMatches, setRouteMatches] = useState([]);
  const [checkingRoutes, setCheckingRoutes] = useState(false);

  // Fetch active trips
  useEffect(() => {
    const q = query(collection(db, "trips"), where("status", "==", "active"));
    const unsub = onSnapshot(q, snap => {
      const now = new Date();
      setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.driverId !== currentUser.id && new Date(t.datetime) > now));
      setLoading(false);
    });
    return unsub;
  }, [currentUser.id]);

  // Fetch my requests
  useEffect(() => {
    const q = query(collection(db, "requests"), where("passengerId", "==", currentUser.id));
    const unsub = onSnapshot(q, snap => setMyReqs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return unsub;
  }, [currentUser.id]);

  // Route-based matching (same as FindTrip.jsx)
  useEffect(() => {
    if (!fromCoords) { setRouteMatches([]); return; }
    const candidates = trips.filter(t => t.fromLat && !t.from.toLowerCase().includes(from.toLowerCase()));
    if (candidates.length === 0) { setRouteMatches([]); return; }
    setCheckingRoutes(true);
    const check = async (trip) => {
      try {
        const [orig, via] = await Promise.all([
          fetch(`https://router.project-osrm.org/route/v1/driving/${trip.fromLon},${trip.fromLat};${trip.toLon},${trip.toLat}?overview=false`).then(r => r.json()),
          fetch(`https://router.project-osrm.org/route/v1/driving/${trip.fromLon},${trip.fromLat};${fromCoords.lon},${fromCoords.lat};${trip.toLon},${trip.toLat}?overview=false`).then(r => r.json()),
        ]);
        if (!orig.routes?.[0] || !via.routes?.[0]) return null;
        return (via.routes[0].distance / orig.routes[0].distance) < 1.12 ? trip : null;
      } catch { return null; }
    };
    Promise.all(candidates.map(check)).then(results => {
      setRouteMatches(results.filter(Boolean));
      setCheckingRoutes(false);
    });
  }, [fromCoords, trips, from]); // eslint-disable-line react-hooks/exhaustive-deps

  // Best request per trip (same logic as FindTrip.jsx)
  const bestReqForTrip = (tripId) => {
    const reqs = myReqs.filter(r => r.tripId === tripId);
    return reqs.find(r => r.status === "accepted") || reqs.find(r => r.status === "pending") || reqs[0] || null;
  };

  // Filtering logic — location + date
  const rejectedTripIds = new Set(myReqs.filter(r => r.status === "rejected").map(r => r.tripId));
  const hasActiveReq = new Set(myReqs.filter(r => r.status === "pending" || r.status === "accepted").map(r => r.tripId));
  hasActiveReq.forEach(id => rejectedTripIds.delete(id));

  const matchesDate = (trip) => {
    if (!filterDate) return true;
    const td = new Date(trip.datetime);
    const fd = new Date(filterDate);
    return td.getFullYear() === fd.getFullYear() && td.getMonth() === fd.getMonth() && td.getDate() === fd.getDate();
  };

  const directMatches = trips.filter(t => {
    if (rejectedTripIds.has(t.id)) return false;
    if (!matchesDate(t)) return false;
    const fm = !from || t.from.toLowerCase().includes(from.toLowerCase());
    const tm = !to   || t.to.toLowerCase().includes(to.toLowerCase());
    return fm && tm;
  });
  const partialMatches = (from && to) ? trips.filter(t => {
    if (rejectedTripIds.has(t.id)) return false;
    if (!matchesDate(t)) return false;
    const fm = t.from.toLowerCase().includes(from.toLowerCase());
    const tm = !t.to.toLowerCase().includes(to.toLowerCase());
    return fm && tm && !directMatches.includes(t);
  }) : [];
  const onRouteMatches = routeMatches.filter(t => !directMatches.includes(t) && !partialMatches.includes(t) && !rejectedTripIds.has(t.id) && matchesDate(t));
  const filtered = [...directMatches, ...partialMatches];
  const totalCount = filtered.length + onRouteMatches.length;

  const dateLabel = filterDate
    ? new Date(filterDate).toLocaleDateString("en", { month: "short", day: "numeric" })
    : null;

  return (
    <>
      {/* ── Hero Banner (c2-hero) ── */}
      <div className="d-hero">
        <div className="d-hero-inner">
          <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 36, color: "#fff", marginBottom: 4 }}>Where are you going?</h1>
          <p style={{ color: "rgba(255,255,255,.65)", fontSize: 15, marginBottom: 0 }}>Find affordable rides across Cyprus</p>

          {/* Search Row (c2-search-row) */}
          <div className="d-search-row">
            <div className="d-search-col">
              <PlaceAutocomplete placeholder="From — e.g. Nicosia" value={from} onChange={v => { setFrom(v); setFromCoords(null); }} onCoords={setFromCoords}/>
            </div>
            <div className="d-search-col">
              <PlaceAutocomplete placeholder="To — e.g. Limassol" value={to} onChange={v => setTo(v)}/>
            </div>
            <div className="d-search-col" style={{ maxWidth: 200 }}>
              <input
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                style={{ width: "100%", border: "none", background: "transparent", padding: "14px 24px 14px 0", fontSize: 14, color: filterDate ? "var(--ink)" : "var(--muted)", outline: "none", fontFamily: "'DM Sans', sans-serif" }}
              />
              {filterDate && (
                <button className="d-search-clear" onClick={() => setFilterDate("")}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              )}
            </div>
            <button className="d-search-btn" onClick={() => {}}>Search</button>
          </div>
        </div>
      </div>

      {/* ── Results Header ── */}
      <div className="d-grid-header">
        <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "var(--ink)" }}>
          {loading ? "Loading rides…" : `Available rides${dateLabel ? ` for ${dateLabel}` : ""}`}
        </span>
        {!loading && (
          <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 12, color: "var(--accent)", padding: "4px 10px", background: "rgba(43,91,224,.08)", borderRadius: 99 }}>
            {totalCount} ride{totalCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* ── Trip Grid (c2-grid) ── */}
      <div className="d-grid">
        {loading ? (
          <>
            <div><TripSkeleton count={1}/></div>
            <div><TripSkeleton count={1}/></div>
            <div><TripSkeleton count={1}/></div>
          </>
        ) : totalCount === 0 ? (
          <div style={{ gridColumn: "1 / -1" }}>
            <EmptyState icon={IC.pin} title="No rides found" description={filterDate ? `No rides on ${dateLabel}. Try a different date or clear the filter.` : "Try different locations or check back later for new rides"}/>
          </div>
        ) : (
          <>
            {filtered.map((trip, i) => (
              <TripCard key={trip.id} trip={trip} myReq={bestReqForTrip(trip.id)} onClick={() => setDetail(trip)} partial={partialMatches.includes(trip)} idx={i}/>
            ))}
            {onRouteMatches.length > 0 && (
              <>
                <div style={{ gridColumn: "1 / -1", fontSize: 11, fontFamily: "Syne", fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                  <span>Passes through your area</span>
                  {checkingRoutes && <span style={{ width: 10, height: 10, border: "1.5px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "igo-spin .7s linear infinite", display: "inline-block" }}/>}
                </div>
                {onRouteMatches.map((trip, i) => (
                  <TripCard key={trip.id} trip={trip} myReq={bestReqForTrip(trip.id)} onClick={() => setDetail(trip)} partial idx={filtered.length + i}/>
                ))}
              </>
            )}
            {checkingRoutes && onRouteMatches.length === 0 && (
              <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "16px 0", color: "var(--muted)", fontSize: 13 }}>Checking nearby routes…</div>
            )}
          </>
        )}
      </div>

      {/* ── Trip Detail Modal ── */}
      {detail && (
        <div className="d-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setDetail(null); }}>
          <div className="d-modal-content">
            <div className="d-modal-header">
              <h3>Trip Details</h3>
              <button className="d-modal-close" onClick={() => setDetail(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <TripDetail trip={detail} currentUser={currentUser} onBack={() => setDetail(null)}/>
          </div>
        </div>
      )}
    </>
  );
};

export default DesktopFind;
